// ./src/components/generic/YearDaySelector/YearDaySelector.tsx
import React, { useCallback, useMemo } from "react";
import {
  Group,
  Text,
  Input,
  Stack,
  ActionIcon,
  Box,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppButton } from "../AppButton";
import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface YearDaySelectorProps {
  /** Selected dates in MM-DD format (e.g., ["01-15", "06-01"]) */
  value: string[];

  /** Callback when selection changes */
  onChange: (value: string[]) => void;

  /** Optional label */
  label?: string;

  /** Optional description */
  description?: string;

  /** Error message */
  error?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size variant */
  size?: "xs" | "sm" | "md" | "lg";

  /** Maximum number of dates allowed (default: 12) */
  maxDates?: number;

  /** Placeholder for empty state */
  placeholder?: string;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Sort MM-DD dates chronologically
 */
const sortDates = (dates: string[]): string[] => {
  return [...dates].sort((a, b) => {
    const [aMonth, aDay] = a.split("-").map(Number);
    const [bMonth, bDay] = b.split("-").map(Number);

    if (aMonth !== bMonth) {
      return aMonth - bMonth;
    }
    return aDay - bDay;
  });
};

/**
 * Validate MM-DD format and check if date is valid
 */
const isValidDate = (mmdd: string): boolean => {
  if (!/^\d{2}-\d{2}$/.test(mmdd)) {
    return false;
  }

  const [month, day] = mmdd.split("-").map(Number);

  if (month < 1 || month > 12) {
    return false;
  }

  // Days per month (using leap year for max validation)
  const daysInMonth = [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  if (day < 1 || day > daysInMonth[month]) {
    return false;
  }

  return true;
};

// ============================================
// MonthDayInput Subcomponent
// ============================================

interface MonthDayInputProps {
  value: string;
  onChange: (value: string) => void;
  onRemove: () => void;
  disabled?: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  monthNames: Record<string, string>;
  error?: boolean;
}

const MonthDayInputComponent: React.FC<MonthDayInputProps> = ({
  value,
  onChange,
  onRemove,
  disabled = false,
  size = "md",
  monthNames,
  error = false,
}) => {
  const theme = useMantineTheme();
  const { t } = useTranslation(["common"]);

  // Parse current value
  const [month, day] = useMemo(() => {
    if (!value) return ["", ""];
    const parts = value.split("-");
    return [parts[0] || "", parts[1] || ""];
  }, [value]);

  // Handle month change
  const handleMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMonth = e.target.value;
      const newValue = newMonth ? `${newMonth}-${day || "01"}` : "";
      onChange(newValue);
    },
    [day, onChange]
  );

  // Handle day change
  const handleDayChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newDay = e.target.value;
      const newValue = month ? `${month}-${newDay}` : "";
      onChange(newValue);
    },
    [month, onChange]
  );

  // Get days for selected month
  const daysInSelectedMonth = useMemo(() => {
    if (!month) return 31;
    const monthNum = parseInt(month, 10);
    const days = [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return days[monthNum] || 31;
  }, [month]);

  const selectStyle: React.CSSProperties = {
    padding: size === "xs" ? "4px 8px" : size === "sm" ? "6px 10px" : "8px 12px",
    fontSize: size === "xs" ? "12px" : size === "sm" ? "13px" : "14px",
    borderRadius: theme.radius.sm,
    border: `1px solid ${error ? theme.colors.red[6] : theme.colors.gray[4]}`,
    backgroundColor: "transparent",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <Group gap="xs" wrap="nowrap">
      {/* Month selector */}
      <select
        value={month}
        onChange={handleMonthChange}
        disabled={disabled}
        style={{ ...selectStyle, minWidth: 100 }}
        aria-label={t("common:selectors.yearDaySelector.month")}
      >
        <option value="">{t("common:yearDaySelector.selectMonth")}</option>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
          <option key={m} value={String(m).padStart(2, "0")}>
            {monthNames[String(m)]}
          </option>
        ))}
      </select>

      {/* Day selector */}
      <select
        value={day}
        onChange={handleDayChange}
        disabled={disabled || !month}
        style={{ ...selectStyle, minWidth: 70 }}
        aria-label={t("common:selectors.yearDaySelector.day")}
      >
        <option value="">{t("common:selectors.yearDaySelector.selectDay")}</option>
        {Array.from({ length: daysInSelectedMonth }, (_, i) => i + 1).map(
          (d) => (
            <option key={d} value={String(d).padStart(2, "0")}>
              {d}
            </option>
          )
        )}
      </select>

      {/* Remove button */}
      <ActionIcon
        variant="subtle"
        color="red"
        size={size}
        onClick={onRemove}
        disabled={disabled}
        aria-label={t("common:selectors.yearDaySelector.removeDate")}
      >
        <AppIcon iconName="X" size={14} />
      </ActionIcon>
    </Group>
  );
};

const MonthDayInput = React.memo(MonthDayInputComponent);

// ============================================
// Main Component
// ============================================

const YearDaySelectorComponent: React.FC<YearDaySelectorProps> = ({
  value,
  onChange,
  label,
  description,
  error,
  required = false,
  disabled = false,
  size = "md",
  maxDates = 12,
  placeholder,
}) => {
  const { t } = useTranslation(["common"]);

  // Get month names from translations
  const monthNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (let i = 1; i <= 12; i++) {
      names[String(i)] = t(`common:months.${i}`);
    }
    return names;
  }, [t]);

  // Handle date change at index
  const handleDateChange = useCallback(
    (index: number, newValue: string) => {
      const newDates = [...value];
      newDates[index] = newValue;

      // Filter out empty values and validate
      const validDates = newDates.filter((d) => d && isValidDate(d));

      // Remove duplicates and sort
      const uniqueDates = [...new Set(validDates)];
      onChange(sortDates(uniqueDates));
    },
    [value, onChange]
  );

  // Handle date removal
  const handleRemoveDate = useCallback(
    (index: number) => {
      const newDates = value.filter((_, i) => i !== index);
      onChange(newDates);
    },
    [value, onChange]
  );

  // Handle add new date
  const handleAddDate = useCallback(() => {
    if (value.length >= maxDates) return;

    // Find a month not yet used, default to January
    let defaultMonth = "01";
    const defaultDay = "01";

    if (value.length > 0) {
      const usedMonths = new Set(value.map((d) => d.split("-")[0]));
      for (let m = 1; m <= 12; m++) {
        const monthStr = String(m).padStart(2, "0");
        if (!usedMonths.has(monthStr)) {
          defaultMonth = monthStr;
          break;
        }
      }
    }

    const newDate = `${defaultMonth}-${defaultDay}`;

    // Only add if not duplicate
    if (!value.includes(newDate)) {
      onChange(sortDates([...value, newDate]));
    }
  }, [value, maxDates, onChange]);

  // Check if can add more
  const canAddMore = value.length < maxDates && !disabled;

  return (
    <Input.Wrapper
      label={label}
      description={description}
      error={error}
      required={required}
      size={size}
    >
      <Stack gap="sm" mt={label || description ? "xs" : 0}>
        {/* Date entries */}
        {value.length === 0 ? (
          <Text size="sm" c="dimmed">
            {placeholder || t("common:selectors.yearDaySelector.noDatesSelected")}
          </Text>
        ) : (
          value.map((date, index) => (
            <MonthDayInput
              key={`${index}-${date}`}
              value={date}
              onChange={(newValue) => handleDateChange(index, newValue)}
              onRemove={() => handleRemoveDate(index)}
              disabled={disabled}
              size={size}
              monthNames={monthNames}
              error={!!error}
            />
          ))
        )}

        {/* Add button */}
        {canAddMore && (
          <Box>
            <AppButton
              variant="tertiary"
              size={size === "lg" ? "sm" : "xs"}
              onClick={handleAddDate}
              iconName="Plus"
              label={t("common:selectors.yearDaySelector.addDate")}
            />
          </Box>
        )}

        {/* Max dates reached message */}
        {value.length >= maxDates && (
          <Text size="xs" c="dimmed">
            {t("common:selectors.yearDaySelector.maxDatesReached", { max: maxDates })}
          </Text>
        )}
      </Stack>
    </Input.Wrapper>
  );
};

export const YearDaySelector = React.memo(YearDaySelectorComponent);